import React, { useState } from "react";
import { Clock, Percent, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Lot 694 — Promos Marketplace : affichage d'une remise temporaire (prix promo, prix barré, badge « -20 % »,
// « Jusqu'au 25 oct. ») et commandes du vendeur (lancer / arrêter). `it.promo` = {price, ends_at, percent} ou null
// (le serveur retire lui-même une promo expirée).
const PROMO_MAX_DAYS = 90;
const dayKey = (d) => d.toISOString().slice(0, 10);

// Lot 700 — règles fixées par l'admin (Admin › Promos Marketplace › Réglages), servies dans la config publique :
// interrupteur `features.marketplace_promos` + `promo_rules` {min_percent, max_percent, max_days, max_active_per_seller}.
export const PROMO_RULE_DEFAULTS = { min_percent: 5, max_percent: 70, max_days: 30, max_active_per_seller: 3 };
export const readPromoRules = (settings) => ({
  ...PROMO_RULE_DEFAULTS, ...(settings?.promo_rules || {}),
  enabled: settings?.features?.marketplace_promos !== false,
  max_days: Math.min(PROMO_MAX_DAYS, Number(settings?.promo_rules?.max_days) || PROMO_RULE_DEFAULTS.max_days),
});
export const usePromoRules = () => { const { config } = useApp(); return readPromoRules(config?.settings); };
// Remise en % d'un prix promo sur un prix de référence (arrondie), null si invalide.
export const promoPercent = (base, promo) => {
  const b = Number(base), p = Number(promo);
  if (!(b > 0) || isNaN(p) || p < 0 || p >= b) return null;
  return Math.round((1 - p / b) * 100);
};

// Champs d'une promo (prix promo + fin) avec aperçu « -20 % · 100 → 80 », rappel des règles et alerte hors règles.
// Utilisé par la fiche (« Lancer une promo ») et par « Vendre un article » (« Promo de lancement », lot 700).
export const PromoFields = ({ basePrice, currency, price, ends, onPrice, onEnds, rules, t, testPrefix = "listing-promo" }) => {
  const today = new Date();
  const min = dayKey(new Date(today.getTime() + 86400000));
  const max = dayKey(new Date(today.getTime() + rules.max_days * 86400000));
  const pct = promoPercent(basePrice, price);
  const outOfRules = pct !== null && (pct < rules.min_percent || pct > rules.max_percent);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">{t("market.promo_price", "Prix promo")} ({currency})</Label>
          <Input type="number" min="0" step="0.01" max={basePrice} value={price} onChange={(e) => onPrice(e.target.value)} className="mt-1" data-testid={`${testPrefix}-price-input`} />
        </div>
        <div>
          <Label className="text-xs">{t("market.promo_end_date", "Fin de la promo")}</Label>
          <Input type="date" min={min} max={max} value={ends} onChange={(e) => onEnds(e.target.value)} className="mt-1" data-testid={`${testPrefix}-ends-input`} />
        </div>
      </div>
      {pct !== null && (
        <div className="text-xs font-semibold" style={{ color: outOfRules ? "#B42318" : "#1B7A3D" }} data-testid={`${testPrefix}-preview`} data-percent={pct} data-out-of-rules={outOfRules ? "1" : "0"}>
          {t("market.promo_badge", "-{n} %").replace("{n}", String(pct))} · <s>{basePrice} {currency}</s> → {Number(price)} {currency}
          {outOfRules && <span className="block font-normal">{t("market.promo_out_of_rules", "La remise doit être entre {min} et {max} %").replace("{min}", String(rules.min_percent)).replace("{max}", String(rules.max_percent))}</span>}
        </div>
      )}
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testPrefix}-rules`}>
        {t("market.promo_rules_hint", "Remise de {min} à {max} %, {days} jours max").replace("{min}", String(rules.min_percent)).replace("{max}", String(rules.max_percent)).replace("{days}", String(rules.max_days))}
      </p>
    </>
  );
};
// Une saisie est-elle acceptable localement (avant l'appel serveur) ?
export const promoInputValid = (basePrice, price, ends, rules) => {
  const pct = promoPercent(basePrice, price);
  return pct !== null && !!ends && pct >= rules.min_percent && pct <= rules.max_percent;
};

export const promoEndsLabel = (promo, t, lang) => {
  if (!promo?.ends_at) return "";
  const end = new Date(promo.ends_at);
  if (dayKey(end) === dayKey(new Date())) return t("market.promo_ends_today", "Dernier jour !");
  return t("market.promo_until", "Jusqu'au {date}").replace("{date}", end.toLocaleDateString(lang || "fr", { day: "numeric", month: "short" }));
};

export const PromoBadge = ({ promo, t, className = "" }) => (
  !promo ? null : (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-extrabold text-white ${className}`} style={{ background: "#D64545" }} data-testid="promo-badge">
      <Percent size={11} strokeWidth={3} /> {t("market.promo_badge", "-{n} %").replace("{n}", String(promo.percent))}
    </span>
  )
);

// Prix d'une annonce : promo en avant + prix de référence barré, sinon prix simple.
export const ListingPrice = ({ it, t, lang, big = false }) => {
  const cls = big ? "text-2xl" : "text-lg";
  if (!it.promo) return <div className={`${cls} font-bold`} style={{ color: "var(--yalola-primary)" }} data-testid="listing-price">{it.price} {it.currency}</div>;
  return (
    <div data-testid="listing-price-promo">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className={`${cls} font-bold`} style={{ color: "#D64545" }} data-testid="listing-promo-price">{it.promo.price} {it.currency}</span>
        <s className={big ? "text-base" : "text-sm"} style={{ color: "var(--yalola-text-muted)" }} data-testid="listing-old-price">{it.price} {it.currency}</s>
        {big && <PromoBadge promo={it.promo} t={t} />}
      </div>
      <div className="text-[11px] inline-flex items-center gap-1 mt-0.5" style={{ color: "#B42318" }} data-testid="listing-promo-until"><Clock size={11} /> {promoEndsLabel(it.promo, t, lang)}</div>
    </div>
  );
};

// Vendeur : « Lancer une promo » (prix promo + date de fin) / « Prolonger » (lot 696 — formulaire pré-rempli, nouvelle
// date ; le serveur remplace le bloc promo → le rappel de la veille repartira) / « Arrêter » → PATCH /marketplace/listings/{id}.
export const PromoControls = ({ it, t, onChanged }) => {
  const rules = usePromoRules(); // lot 700 — interrupteur + règles admin
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [ends, setEnds] = useState("");
  const [saving, setSaving] = useState(false);
  const send = async (promo, okMsg) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/marketplace/listings/${it.id}`, { promo });
      onChanged(data.listing);
      setOpen(false); setPrice(""); setEnds("");
      toast.success(okMsg);
    } catch (e) { toast.error(e?.friendlyMessage || t("market.promo_invalid", "Indique un prix promo inférieur au prix et une date de fin à venir")); }
    setSaving(false);
  };
  const start = () => {
    if (!promoInputValid(it.price, price, ends, rules)) {
      const pct = promoPercent(it.price, price);
      toast.error(pct !== null && ends
        ? t("market.promo_out_of_rules", "La remise doit être entre {min} et {max} %").replace("{min}", String(rules.min_percent)).replace("{max}", String(rules.max_percent))
        : t("market.promo_invalid", "Indique un prix promo inférieur au prix et une date de fin à venir"));
      return;
    }
    send({ price: parseFloat(price), ends_at: new Date(`${ends}T23:59:59`).toISOString() }, it.promo ? t("market.promo_extended", "Promo prolongée ✅") : t("market.promo_started", "Promo lancée 🎉"));
  };
  const extend = () => { setPrice(String(it.promo.price)); setEnds(""); setOpen(true); };
  if (!rules.enabled) return null; // lot 700 — promos désactivées par l'admin : aucune commande (la promo éventuelle est masquée par le serveur)
  // Lot 698 — deux colonnes ÉGALES (grid) et boutons qui peuvent rétrécir (`min-w-0`) : en flex-1, un bouton ne descend
  // jamais sous la largeur de son texte → « Arrêter la promo » débordait du cadre sur 390 px. Le libellé passe sur 2 lignes
  // si la langue est plus longue, jamais hors du bouton.
  const twoCols = "rounded-full min-w-0 h-auto min-h-9 py-1.5 px-3 whitespace-normal leading-tight text-[13px]";
  if (it.promo && !open) {
    return (
      <div className="grid grid-cols-2 gap-2" data-testid="listing-promo-actions">
        <Button variant="outline" className={twoCols} disabled={saving} onClick={extend} data-testid="listing-promo-extend">
          <Clock size={15} className="shrink-0" /> <span className="text-left">{t("market.promo_extend", "Prolonger la promo")}</span>
        </Button>
        <Button variant="outline" className={twoCols} disabled={saving} onClick={() => send(null, t("market.promo_stopped", "Promo arrêtée"))} data-testid="listing-promo-stop">
          {saving ? <Loader2 size={15} className="shrink-0 animate-spin" /> : <Percent size={15} className="shrink-0" />} <span className="text-left">{t("market.promo_stop", "Arrêter la promo")}</span>
        </Button>
      </div>
    );
  }
  if (!open) {
    return (
      <Button variant="outline" className="w-full rounded-full" onClick={() => setOpen(true)} data-testid="listing-promo-start">
        <Percent size={15} className="mr-1.5" /> {t("market.promo_start", "Lancer une promo")}
      </Button>
    );
  }
  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="listing-promo-form">
      <PromoFields basePrice={it.price} currency={it.currency} price={price} ends={ends} onPrice={setPrice} onEnds={setEnds} rules={rules} t={t} />
      <div className="flex gap-2">
        <Button className="pill-primary rounded-full flex-1" disabled={saving} onClick={start} data-testid="listing-promo-save">{saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} className="mr-1" />} {it.promo ? t("market.promo_extend", "Prolonger la promo") : t("market.promo_start", "Lancer une promo")}</Button>
        <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="listing-promo-cancel">{t("common.cancel", "Annuler")}</Button>
      </div>
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("market.promo_hint", "Le prix barré reste visible ; les favoris sont prévenus de la baisse 🔔")}</p>
    </div>
  );
};
